import { Card, Col, Divider, Row, Typography } from 'antd';
import { dateUtils, generateUniversalUrl } from 'common/helpers';
import { ActionState, EventDetail, ShareDeeplinkType } from "common/types";
import StyledContainer from 'components/container';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { isAndroid, isIOS } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useGetEventDetailData, useResetEventDetailData } from 'recoil/hooks/useEventDetailData';
import { useGetFetchError } from 'recoil/hooks/useFetchError';
import { useEventMutations } from 'recoil/mutations/useEventMutations';
import ButtonJoin from './ButtonJoin';
import ErrorFetching from './ErrroFetching';
import NotFound from './NotFound';
import PanelInfo from './PanelEventInfo';
import ReferDownload from './ReferToDownload';

const { Text } = Typography;

interface UniversalURL {
  androidDeepLink: string;
  iOSDeepLink: string;
  playStoreLink: string;
  appStoreLink: string;
}


const EventShareDetail: FC = () => {

  const { t } = useTranslation();
  const { getEventDetailData } = useEventMutations();
  const error = useGetFetchError();
  const eventDetailData = useGetEventDetailData();
  const resetData = useResetEventDetailData();
  const [loading, setLoading] = useState<boolean>(false);
  const { id } = useParams<{ id?: string }>();
  const [currentError, setError] = useState<ActionState>(ActionState.NotSet);

  const [eventData, setEventData] = useState<EventDetail>({
    id: '',
    name: '',
    description: '',
    content: '',
    disabled: false,
    image: '',
    relatedShopLink: '',
  });
  const [universalLink, setUniversalLink] = useState<UniversalURL>({
    appStoreLink: '',
    playStoreLink: '',
    androidDeepLink: '',
    iOSDeepLink: ''
  });

  const fetchEventData = useCallback(async (id: string) => {
    setLoading(true);
    await getEventDetailData(id);
  }, []);

  const onClickDeepLink = () => {
    if (isAndroid) {
      window.location.href = universalLink.androidDeepLink;
    } else if (isIOS) {
      window.location.href = universalLink.iOSDeepLink;
    } else {
      window.location.reload();
    }
  };

  const isEventExpired = () => {
    if (error === ActionState.OK) {
      return eventData.disabled || dateUtils.dayjs().isSameOrAfter(eventData.endDate, 'milliseconds');
    } else {
      return true;
    }
  };

  useEffect(() => {

    if (error !== null || typeof error !== 'undefined') {
      setLoading(false);
      setError(error);
    }

  }, [error]);

  useEffect(() => {
    if (id) {
      fetchEventData(id);
      const { androidDeepLink, iOSDeepLink, playstore, appstore } = generateUniversalUrl(id, ShareDeeplinkType.Event);
      setUniversalLink({
        androidDeepLink,
        iOSDeepLink,
        playStoreLink: playstore,
        appStoreLink: appstore
      });
    } else {
      setError(ActionState.NotFound);
    }

    return () => {
      resetData();
    }
  }, [id]);

  useEffect(() => {
    if (eventDetailData) {
      setEventData(eventDetailData);
    }
  }, [eventDetailData]);

  useEffect(() => {
    document.title = `今すぐイベントに参加してください！ - EMOSHARE`;
  }, []);

  const renderCardContent = () => {
    if (error === ActionState.OK) {
      return (
        <>
          <PanelInfo eventDetailData={eventDetailData} />
          <Row justify='center' align='middle' style={{ marginTop: '5px' }}>
            <Col>
              <Text strong type='success'>{t('RESOURCES.EVENT.LABELS.PARTICIPATE')}</Text>
            </Col>
          </Row>
          <Row align='middle' gutter={[16, 16]} justify='center' style={{ marginTop: '10px' }}>
            <Col>
              <ButtonJoin
                onClick={onClickDeepLink}
                eventExpiredOrHidden={isEventExpired()}
                displayText={eventData.disabled ? t('RESOURCES.EVENT.BUTTONS.HIDDEN') : t('RESOURCES.EVENT.BUTTONS.JOIN')}
              />
            </Col>
            <Col lg={24} sm={24} md={24} xs={24}>
              <Divider plain>
                <Text type='warning'><span dangerouslySetInnerHTML={{ __html: t('RESOURCES.EVENT.LABELS.HELPER_DOWNLOAD') }}></span></Text>
              </Divider>
            </Col>
            <Col>
              <ReferDownload playstore={universalLink.playStoreLink} appstore={universalLink.appStoreLink} />
            </Col>
          </Row>
        </>
      );
    } else if (error === ActionState.ServerError) {
      return (<ErrorFetching />);
    } else {
      return (<NotFound />);
    }
  }

  const renderCardCover = () => {
    if (loading) {
      return null;
    } else {
      if (currentError === ActionState.OK) {
        const imageData = eventData.image;
        if (imageData) {
          return <img alt='cover' style={{ filter: 'brightness(70%)' }} src={imageData} />;
        } else {
          return null;
        }
      } else {
        return null
      }
    }
  };

  return (
    <StyledContainer>
      <Row align='middle' justify='center'>
        <Col xxl={8} xl={8} lg={24} md={24} sm={24} xs={24}>
          <Card
            loading={loading}
            cover={renderCardCover()}>
            {
              renderCardContent()
            }
          </Card>
        </Col>
      </Row>
    </StyledContainer>
  );
};

export default EventShareDetail;